'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Bot } from 'lucide-react';

/* ---------- Avatar ---------- */

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  fallback?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  isAgent?: boolean;
  /** 角色色背景(无图首字母/agent):consumer→brand 粉,provider→provider 蓝。 */
  tone?: 'brand' | 'provider';
}

const sizeMap = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
} as const;

export function Avatar({
  src,
  alt = '',
  fallback,
  size = 'md',
  isAgent = false,
  tone = 'brand',
  className,
  ...props
}: AvatarProps) {
  const [imgError, setImgError] = React.useState(false);

  const showFallback = !src || imgError;

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        tone === 'provider'
          ? 'bg-gradient-to-br from-provider-400 to-provider-600'
          : 'bg-gradient-to-br from-brand-400 to-brand-600',
        sizeMap[size],
        className
      )}
      {...props}
    >
      {!showFallback && (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      )}
      {showFallback && (
        isAgent ? (
          <Bot className="h-1/2 w-1/2 text-white" />
        ) : (
          <span className="font-medium text-white" aria-hidden="true">
            {fallback?.charAt(0)?.toUpperCase() ?? '?'}
          </span>
        )
      )}
    </div>
  );
}

/* ---------- AvatarGroup ---------- */

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  max?: number;
  children: React.ReactNode;
}

export function AvatarGroup({
  max = 4,
  children,
  className,
  ...props
}: AvatarGroupProps) {
  const childArray = React.Children.toArray(children);
  const visible = childArray.slice(0, max);
  const overflow = childArray.length - max;

  return (
    <div className={cn('flex -space-x-2', className)} {...props}>
      {visible.map((child, i) => (
        <div key={i} className="ring-2 ring-background rounded-full">
          {child}
        </div>
      ))}
      {overflow > 0 && (
        <div
          className={cn(
            'relative inline-flex shrink-0 items-center justify-center rounded-full',
            'bg-surface-2 text-muted-foreground text-xs font-medium',
            'h-8 w-8 ring-2 ring-background'
          )}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}
